import styled from '@emotion/styled';

import { theme, Typography } from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';
import type { StudentReport } from '../../modules/fluency/deck-report';

type OptionType = {
  value: string;
  label: string;
};

type Props = {
  isVisible?: boolean;
  students: StudentReport[];
  onStudentClick?: (option: OptionType | null) => void;
};

const Container = styled.div`
  display: flex;
  padding: 8px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  border-radius: 4px;
  width: 620px;
  background-color: ${theme.tokens.color.alias.cm.surface['surface-secondary']
    .value};
`;

const DescriptionContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const StudentsGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
`;

const StudentNameContainer = styled.div`
  min-height: 24px;
  align-items: center;
`;

const StudentName = styled.a`
  display: inline-flex;
  color: ${theme.tokens.color.alias.cm.text['text-subtle'].value};
  font-size: 14px;
  text-decoration: underline;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  &:hover {
    color: ${theme.tokens.color.alias.cm.text.text.value};
  }
`;

const ItalicBody3 = styled(Typography.Body3)`
  font-style: italic;
`;

function AdaptedStudentsBox({
  isVisible = true,
  students,
  onStudentClick,
}: Props): JSX.Element | null {
  const { t } = useTranslation();

  if (!isVisible) {
    return null;
  }

  const handleStudentClick = (student: StudentReport): void => {
    onStudentClick?.({
      value: student.uuid,
      label: `${student.firstName} ${student.lastName}`,
    });
  };

  return (
    <Container>
      <DescriptionContainer>
        <Typography.Body1>
          {t('fluency.reports.adaptedStudents.title')}
        </Typography.Body1>
        <ItalicBody3>
          {`(${t('fluency.reports.adaptedStudents.description')})`}
        </ItalicBody3>
      </DescriptionContainer>
      <StudentsGrid>
        {students.map((student) => (
          <StudentNameContainer
            key={student.uuid}
            onClick={() => handleStudentClick(student)}
          >
            <StudentName>{`${student.firstName} ${student.lastName}`}</StudentName>
          </StudentNameContainer>
        ))}
      </StudentsGrid>
    </Container>
  );
}

export { AdaptedStudentsBox };
